import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { apiClient, buildQueryString, withMinDelayThunk, withMinDelay } from '../../utils/api/';
import { REMINDER_STATUSES, REMINDER_STATUS_FILTER_ACTIVE_OR_PAUSED } from 'constants/reminders';

const DEFAULT_REMINDER_FILTERS = {
  status: REMINDER_STATUS_FILTER_ACTIVE_OR_PAUSED,
  type: 'ALL',
  search: '',
};

// Async Thunks

export const fetchReminders = createAsyncThunk(
  'reminders/fetchReminders',
  async (params, { rejectWithValue, getState }) => {
    try {
      const { initialFetchDone } = getState().reminders;
      const qs = buildQueryString(params);
      const url = qs ? `/reminders?${qs}` : '/reminders';
      const response = !initialFetchDone
        ? await withMinDelay(apiClient.get(url))
        : await apiClient.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch reminders');
    }
  }
);

export const fetchReminderById = createAsyncThunk(
  'reminders/fetchReminderById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/reminders/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch reminder');
    }
  }
);

// Create a new reminder
export const createReminder = createAsyncThunk(
  'reminders/createReminder',
  withMinDelayThunk(async (reminderData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/reminders', reminderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create reminder');
    }
  })
);

// Update an existing reminder
export const updateReminder = createAsyncThunk(
  'reminders/updateReminder',
  withMinDelayThunk(async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/reminders/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update reminder');
    }
  })
);

// Delete a reminder
export const deleteReminder = createAsyncThunk(
  'reminders/deleteReminder',
  withMinDelayThunk(async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/reminders/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete reminder');
    }
  })
);

// Set the worker on a reminder. Pass workerId: null to detach.
export const setReminderWorker = createAsyncThunk(
  'reminders/setReminderWorker',
  withMinDelayThunk(async ({ reminderId, workerId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/reminders/${reminderId}/worker`, { workerId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update reminder worker');
    }
  })
);

export const setReminderTodo = createAsyncThunk(
  'reminders/setReminderTodo',
  withMinDelayThunk(async ({ reminderId, todoId, todoControlsReminder }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/reminders/${reminderId}/todo`, { todoId, todoControlsReminder });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update reminder todo');
    }
  })
);

export const cloneReminder = createAsyncThunk(
  'reminders/cloneReminder',
  withMinDelayThunk(async ({ reminderId, subject }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/reminders/${reminderId}/clone`, { subject });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to clone reminder');
    }
  })
);

const initialState = {
  items: [],
  pageInfo: null,
  filters: DEFAULT_REMINDER_FILTERS,
  statusFilterValue: DEFAULT_REMINDER_FILTERS.status,
  typeFilterValue: DEFAULT_REMINDER_FILTERS.type,
  searchFilterValue: DEFAULT_REMINDER_FILTERS.search,
  loading: false,
  error: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  initialFetchDone: false,
  pendingUpdatesById: {},
};

const remindersSlice = createSlice({
  name: 'reminders',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      const nextFilters = {
        ...DEFAULT_REMINDER_FILTERS,
        ...(state.filters || {}),
        ...action.payload,
      };

      state.filters = nextFilters;
      state.statusFilterValue = nextFilters.status;
      state.typeFilterValue = nextFilters.type;
      state.searchFilterValue = nextFilters.search;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetStatus: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Reminders
      .addCase(fetchReminders.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pageInfo = action.payload.pageInfo || null;
        state.status = 'succeeded';
        state.initialFetchDone = true;
      })
      .addCase(fetchReminders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Fetch Reminder by ID
      .addCase(fetchReminderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReminderById.fulfilled, (state, action) => {
        state.loading = false;
        const reminder = action.payload.data;
        const index = state.items.findIndex((item) => item._id === reminder._id);
        if (index !== -1) {
          state.items[index] = reminder;
        } else {
          state.items.push(reminder);
        }
        state.status = 'succeeded';
      })
      .addCase(fetchReminderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Create Reminder
      .addCase(createReminder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(createReminder.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload.data);
        state.status = 'succeeded';
      })
      .addCase(createReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Update Reminder (optimistic)
      .addCase(updateReminder.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
        const { id, data } = action.meta.arg;
        const index = state.items.findIndex((item) => item._id === id);
        if (index === -1) return;
        if (!state.pendingUpdatesById) state.pendingUpdatesById = {};
        state.pendingUpdatesById[id] = state.items[index];
        state.items[index] = { ...state.items[index], ...data };
      })
      .addCase(updateReminder.fulfilled, (state, action) => {
        state.loading = false;
        const reminder = action.payload.data;
        if (state.pendingUpdatesById) delete state.pendingUpdatesById[reminder._id];
        const index = state.items.findIndex((item) => item._id === reminder._id);
        if (index !== -1) {
          state.items[index] = reminder;
        }
        state.status = 'succeeded';
      })
      .addCase(updateReminder.rejected, (state, action) => {
        state.loading = false;
        const { id } = action.meta.arg;
        const snapshot = state.pendingUpdatesById?.[id];
        if (snapshot) {
          const index = state.items.findIndex((item) => item._id === id);
          if (index !== -1) {
            state.items[index] = snapshot;
          }
          delete state.pendingUpdatesById[id];
        }
        state.error = action.payload;
        state.status = 'failed';
      })

      // Delete Reminder
      .addCase(deleteReminder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(deleteReminder.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item._id !== action.payload);
        state.status = 'succeeded';
      })
      .addCase(deleteReminder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Set Worker (attach or detach)
      .addCase(setReminderWorker.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(setReminderWorker.fulfilled, (state, action) => {
        state.loading = false;
        const reminder = action.payload.data;
        const index = state.items.findIndex((item) => item._id === reminder._id);
        if (index !== -1) {
          state.items[index] = reminder;
        }
        state.status = 'succeeded';
      })
      .addCase(setReminderWorker.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      .addCase(setReminderTodo.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(setReminderTodo.fulfilled, (state, action) => {
        state.loading = false;
        const reminder = action.payload.data;
        const index = state.items.findIndex((item) => item._id === reminder._id);
        if (index !== -1) {
          state.items[index] = reminder;
        }
        state.status = 'succeeded';
      })
      .addCase(setReminderTodo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      .addCase(cloneReminder.fulfilled, (state, action) => {
        state.items.push(action.payload.data);
      })
      .addCase(cloneReminder.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  resetStatus,
  setFilters,
} = remindersSlice.actions;

// Selectors
export const selectAllReminders = (state) => state.reminders.items;
export const selectPageInfo = (state) => state.reminders.pageInfo;
export const selectRemindersLoading = (state) => state.reminders.loading;
export const selectRemindersInitialFetchDone = (state) => state.reminders.initialFetchDone;
export const selectRemindersError = (state) => state.reminders.error;
export const selectRemindersStatus = (state) => state.reminders.status;
const selectRemindersState = (state) => state.reminders;

export const selectRemindersFilters = createSelector(
  [selectRemindersState],
  (remindersState) => {
    if (remindersState.filters) {
      return remindersState.filters;
    }

    return {
      status: remindersState.statusFilterValue ?? DEFAULT_REMINDER_FILTERS.status,
      type: remindersState.typeFilterValue ?? DEFAULT_REMINDER_FILTERS.type,
      search: remindersState.searchFilterValue ?? DEFAULT_REMINDER_FILTERS.search,
    };
  }
);

const EMPTY_PENDING_UPDATES = {};
export const selectPendingReminderUpdates = (state) =>
  state.reminders.pendingUpdatesById || EMPTY_PENDING_UPDATES;

export const selectAttachableReminders = createSelector(
  [selectAllReminders],
  (reminders) => reminders.filter((reminder) => (
    reminder.status === REMINDER_STATUSES.ACTIVE || reminder.status === REMINDER_STATUSES.PAUSED
  ))
);

export default remindersSlice.reducer;
